import { LocationRoomViewer } from "@/components/locations/location-room-viewer"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { useTranslation } from "@/hooks/use-translation"
import type { RoomViewerCameraState } from "@/lib/location-room-viewer/engine"
import type { LocationCardinalFacing, LocationViewpointRow } from "@/lib/locations-api"

type LocationRoomViewerDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  locationName: string
  variantName?: string | null
  wallUrls: Partial<Record<LocationCardinalFacing, string | null>>
  wallBusy: Partial<Record<LocationCardinalFacing, boolean>>
  onGenerateWall: (facing: LocationCardinalFacing) => void
  wallGenerateBlockedReason?: (facing: LocationCardinalFacing) => string | null
  confirmDelayKeyForWall: (facing: LocationCardinalFacing) => string
  panoramaUrl?: string | null
  panoramaBusy?: boolean
  onGeneratePanorama?: () => void
  panoramaGenerateBlockedReason?: string | null
  confirmDelayKeyForPanorama?: string
  panoramaVersionCount?: number
  onOpenPanoramaVersions?: () => void
  modelLabel?: string | null
  viewpoints?: LocationViewpointRow[]
  onCreateViewpoint?: (blob: Blob, cameraMeta: RoomViewerCameraState) => void | Promise<void>
  onRemoveViewpoint?: (viewpointId: number) => void | Promise<void>
  onRenameViewpoint?: (viewpointId: number, name: string) => void | Promise<void>
  creatingViewpoint?: boolean
}

export function LocationRoomViewerDialog({
  open,
  onOpenChange,
  locationName,
  variantName,
  wallUrls,
  wallBusy,
  onGenerateWall,
  wallGenerateBlockedReason,
  confirmDelayKeyForWall,
  panoramaUrl,
  panoramaBusy,
  onGeneratePanorama,
  panoramaGenerateBlockedReason,
  confirmDelayKeyForPanorama,
  panoramaVersionCount,
  onOpenPanoramaVersions,
  modelLabel,
  viewpoints,
  onCreateViewpoint,
  onRemoveViewpoint,
  onRenameViewpoint,
  creatingViewpoint,
}: LocationRoomViewerDialogProps) {
  const { t } = useTranslation(["workspace"])

  const title =
    variantName != null && variantName.trim() !== ""
      ? t("locationsPage.roomViewer.titleWithVariant", { name: locationName, variant: variantName })
      : t("locationsPage.roomViewer.title", { name: locationName })

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={t("locationsPage.roomViewer.description")}
      closeAriaLabel={t("locationsPage.roomViewer.close")}
      size="full"
      maxHeightClassName="h-[min(92vh,56rem)]"
      bodyClassName="flex min-h-0 flex-1 flex-col p-4 pt-3"
      contentClassName="overflow-hidden"
    >
      {open ? (
        <LocationRoomViewer
          wallUrls={wallUrls}
          wallBusy={wallBusy}
          onGenerateWall={onGenerateWall}
          wallGenerateBlockedReason={wallGenerateBlockedReason}
          confirmDelayKeyForWall={confirmDelayKeyForWall}
          panoramaUrl={panoramaUrl}
          panoramaBusy={panoramaBusy}
          onGeneratePanorama={onGeneratePanorama}
          panoramaGenerateBlockedReason={panoramaGenerateBlockedReason}
          confirmDelayKeyForPanorama={confirmDelayKeyForPanorama}
          panoramaVersionCount={panoramaVersionCount}
          onOpenPanoramaVersions={onOpenPanoramaVersions}
          modelLabel={modelLabel}
          viewpoints={viewpoints}
          onCreateViewpoint={onCreateViewpoint}
          onRemoveViewpoint={onRemoveViewpoint}
          onRenameViewpoint={onRenameViewpoint}
          creatingViewpoint={creatingViewpoint}
        />
      ) : null}
    </WorkspaceDialog>
  )
}
